/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, August 2nd 2024, 11:42:31 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { logger } from "lib/logger";
import { useCopyTextToClipBoard } from "lib/utils";
import { OSIconButton } from "ui-widgets";

import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-sql";
import "./SQLEditor.css"; // https://stackoverflow.com/questions/20931029/ace-editor-monospace-fonts-issues-with-cursor-spacing

type SQLEditorProps = {
  query: string;
  onChangeQuery: (query: string) => void;
  onRunQuery?: () => void;
  editorId: string;
};

export const SQLEditor = ({ editorId, query, onChangeQuery, onRunQuery }: SQLEditorProps) => {
  let textColor = "var(--grey-04)";
  const queryEditorBackgroundColor = "rgb(249, 251, 252)";
  const { copied, setCopied, copyTextToClipBoard } = useCopyTextToClipBoard();
  return (
    <>
      <Stack sx={{ background: queryEditorBackgroundColor }}>
        <AceEditor
          mode="sql"
          value={query}
          onChange={onChangeQuery}
          onCopy={() => setCopied(true)}
          name={editorId} // Unique Id to be used for the editor: https://github.com/securingsincity/react-ace/blob/master/docs/Ace.md#available-props
          editorProps={{ $blockScrolling: true, $enableBlockSelect: false }}
          style={{
            width: "100%",
            height: "300px",
            fontSize: "0.75rem",
            color: textColor,
            backgroundColor: queryEditorBackgroundColor,
            border: "none",
            paddingTop: "0.5rem",
            fontFamily: "monospace", // https://stackoverflow.com/questions/20931029/ace-editor-monospace-fonts-issues-with-cursor-spacing
          }}
          showPrintMargin={false}
        />
        <Divider />
        {/*Query Actions */}
        <Stack direction="row" spacing={2}>
          <OSIconButton
            color="primary"
            toolTipText={<Typography>Run Query</Typography>}
            icon={<PlayArrowIcon fontSize="large" />}
            onClick={onRunQuery}
          />
          <OSIconButton
            color="primary"
            toolTipText={<Typography>Copy Query</Typography>}
            icon={<ContentCopyIcon />}
            onClick={() => copyTextToClipBoard(query)}
          />
        </Stack>
      </Stack>
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={2000}
        message="Query Copied to Clipboard"
      />
    </>
  );
};

type JSONEditorProps = {
  editorId: string;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
};

export const JSONEditor = ({ editorId, value, onChange }: JSONEditorProps) => {
  let textColor = "var(--grey-04)";
  const editorBackgroundColor = "rgb(249, 251, 252)";
  const { copied, setCopied } = useCopyTextToClipBoard();
  let jsonStringValue = "";
  try {
    jsonStringValue = JSON.stringify(value, null, 2);
  } catch (error) {
    logger.error("Error trying to stringify JSON", error);
  }

  const onChangeJSONString = (value: string, event: any) => {
    try {
      onChange(JSON.parse(value));
    } catch (error) {
      logger.error("Error parsing JSON", error);
    }
  };

  return (
    <>
      <Stack sx={{ background: editorBackgroundColor }}>
        <AceEditor
          mode="json"
          value={jsonStringValue}
          onChange={onChangeJSONString}
          onCopy={() => setCopied(true)}
          name={editorId} // Unique Id to be used for the editor:
          editorProps={{ $blockScrolling: true, $enableBlockSelect: false }}
          style={{
            width: "100%",
            height: "300px",
            fontSize: "0.75rem",
            color: textColor,
            backgroundColor: editorBackgroundColor,
            border: "none",
            paddingTop: "0.5rem",
            fontFamily: "monospace", // https://stackoverflow.com/questions/20931029/ace-editor-monospace-fonts-issues-with-cursor-spacing
          }}
          showPrintMargin={false}
        />
      </Stack>
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={2000}
        message="JSON Copied to Clipboard"
      />
    </>
  );
};
